import { createAsyncThunk } from '@reduxjs/toolkit'
import { generateClientSeed } from 'helpers/common.helpers'
import { debitWalletAmt } from 'redux-store/redux/slices/auth.slice'
import { setNewClientSeed, setServerSeedHash } from 'redux-store/redux/slices/gameSettings.slice'
import { DEFAULT_VARIANT, VARIANT_META } from '../constants'
import {
  getMyBetsThreeKingsService,
  getTopWinsThreeKingsService,
  placeBetThreeKingsService
} from '../threeKingsScratch.service'

const toNumberArray = (value) => {
  if (Array.isArray(value)) return value.map((item) => Number(item)).filter((item) => Number.isFinite(item))
  if (typeof value === 'string') {
    try {
      return toNumberArray(JSON.parse(value))
    } catch {
      return []
    }
  }
  return []
}

export const normalizeRound = (res, fallbackBet = 0) => {
  if (!res || typeof res !== 'object') return null
  const luckySymbols = toNumberArray(res.luckySymbols)
  const mainField = toNumberArray(res.mainField)
  if (!luckySymbols.length || !mainField.length) return null

  const multiplier = Number(res.multiplier || 0)
  const winningAmount = Number(res.winningAmount || 0)
  const variant = String(res.variant || DEFAULT_VARIANT).toUpperCase()
  const variantFixedBet = VARIANT_META[variant]?.fixedBet || 5
  const rawBet = Number(res.betAmount ?? res.bet ?? res.amount ?? fallbackBet ?? 0)
  const bet = rawBet > 0 ? rawBet : variantFixedBet

  return {
    ...res,
    betId: Number(res.betId || res.id || 0),
    betAmount: bet,
    winningAmount,
    multiplier,
    isWin: Boolean(res.isWin) || multiplier > 0,
    variant,
    luckySymbols,
    mainField,
    matchedSymbols: toNumberArray(res.matchedSymbols),
    fieldPrizes: toNumberArray(res.fieldPrizes),
    rows: Number(res.rows || 0) || undefined,
    columns: Number(res.columns || 0) || undefined,
    fieldSize: Number(res.fieldSize || mainField.length || 0)
  }
}

const normalizeBetList = (res) => {
  const betsList = res?.bets ?? res?.rows ?? (Array.isArray(res) ? res : [])
  if (!Array.isArray(betsList)) return []
  return betsList.map((item) => {
    const variant = String(item.variant || DEFAULT_VARIANT).toUpperCase()
    const variantFixedBet = VARIANT_META[variant]?.fixedBet || 5
    const rawBet = Number(item.betAmount ?? item.bet ?? item.amount ?? item.wager ?? 0)
    const betAmount = rawBet > 0 ? rawBet : variantFixedBet
    return {
      ...item,
      variant,
      betAmount
    }
  })
}

export const placeBet = createAsyncThunk('three-kings-scratch/placeBet', async ({ payload }, thunkApi) => {
  try {
    const { auth } = thunkApi.getState()
    const isDemoGame = Boolean(auth.demoMode || auth.userInfo?.demoMode)
    const res = await placeBetThreeKingsService({ data: payload, isDemoGame })
    const variant = String(payload?.variant || res?.variant || DEFAULT_VARIANT).toUpperCase()
    const fallbackBet = Number(payload?.betAmount || VARIANT_META[variant]?.fixedBet || 5)
    const round = normalizeRound(res, fallbackBet)
    if (!round) {
      throw [{ description: 'Empty bet result. Please try again.' }]
    }

    if (round.nextServerSeedHash) {
      thunkApi.dispatch(setNewClientSeed(generateClientSeed()))
      thunkApi.dispatch(setServerSeedHash(round.nextServerSeedHash))
    }

    const betAmountToDebit = Number(round.betAmount || fallbackBet)
    thunkApi.dispatch(debitWalletAmt(betAmountToDebit))
    return {
      round,
      betRecord: {
        ...round,
        betAmount: betAmountToDebit,
        variant,
        createdAt: round.createdAt || round.created_at || new Date().toISOString()
      }
    }
  } catch (error) {
    return thunkApi.rejectWithValue(error?.[0]?.description || error?.message || 'Bet failed')
  }
})

export const getMyBets = createAsyncThunk('three-kings-scratch/myBets', async (params = { limit: 10, offset: 0 }, thunkApi) => {
  try {
    const res = await getMyBetsThreeKingsService(params)
    return normalizeBetList(res)
  } catch (error) {
    return thunkApi.rejectWithValue(error?.[0]?.description || error?.message)
  }
})

export const getTopBets = createAsyncThunk('three-kings-scratch/topBets', async (params = { limit: 10, offset: 0 }, thunkApi) => {
  try {
    const res = await getTopWinsThreeKingsService(params)
    return normalizeBetList(res)
  } catch (error) {
    return thunkApi.rejectWithValue(error?.[0]?.description || error?.message)
  }
})
